import { useState } from "react";
import { LoaderCircle } from "lucide-react";
import type { AuthProfile } from "@/types/api";
import {
  SettingsCard,
  SettingsNote,
  SettingsPanel,
  SettingsRow,
  SettingsToggle,
} from "../SettingsUI";

function accountTypeLabel(profile: AuthProfile) {
  if (profile.is_superuser) return "Superuser";
  if (profile.is_supervisor) return "Supervisor";
  if (profile.is_ai_agent) return "AI agent";
  return "Agent";
}

export function AccountSection({ profile }: { profile: AuthProfile | null }) {
  const [securityAlerts, setSecurityAlerts] = useState(true);
  const [twoStep, setTwoStep] = useState(false);
  const [loginAlerts, setLoginAlerts] = useState(true);

  if (!profile) {
    return (
      <SettingsPanel title="Account" description="Security notifications, account info">
        <div className="flex items-center justify-center gap-2 py-16 text-sm text-fg-muted">
          <LoaderCircle className="h-4 w-4 animate-spin" />
          Loading account…
        </div>
      </SettingsPanel>
    );
  }

  const groups =
    profile.groups?.map((group) => group.name).filter(Boolean).join(", ") || "No groups";

  return (
    <SettingsPanel title="Account" description="Security notifications, account info">
      <SettingsCard title="Account info">
        <SettingsRow
          title="Name"
          description={profile.name}
          trailing={
            <span className="text-xs text-fg-subtle">
              {profile.is_active === false ? "Inactive" : "Active"}
            </span>
          }
        />
        <SettingsRow title="Email" description={profile.email} />
        <SettingsRow title="Username" description={profile.username} />
        <SettingsRow title="Phone" description={profile.ph_no || "Not set"} />
        <SettingsRow title="Account type" description={accountTypeLabel(profile)} />
        <SettingsRow
          title="Organization"
          description={profile.organization?.name || "No organization"}
        />
        <SettingsRow title="Country" description={profile.country?.name || "Not set"} />
        <SettingsRow title="Groups" description={groups} />
        <SettingsRow
          title="Current status"
          description={profile.current_status || profile.allow || "Unavailable"}
        />
      </SettingsCard>

      <SettingsCard title="Security">
        <SettingsToggle
          title="Security notifications"
          description="Get alerted when your account is used on a new device"
          checked={securityAlerts}
          onChange={setSecurityAlerts}
        />
        <SettingsToggle
          title="Login alerts"
          description="Email me when someone signs in with my credentials"
          checked={loginAlerts}
          onChange={setLoginAlerts}
        />
        <SettingsToggle
          title="Two-step verification"
          description="Add an extra layer of protection when signing in"
          checked={twoStep}
          onChange={setTwoStep}
        />
        <SettingsRow title="Change password" description="Update your sign-in password" />
        <SettingsRow title="Active sessions" description="Manage devices signed into your account" />
      </SettingsCard>

      <SettingsCard title="Account actions">
        <SettingsRow title="Request account info" description="Download a report of your account data" />
        <SettingsRow
          title="Delete account"
          description="Permanently remove your account and data"
          danger
        />
      </SettingsCard>

      <SettingsNote>
        Account details above come from your live Papaya profile. Security toggles are local UI for now.
      </SettingsNote>
    </SettingsPanel>
  );
}
